import { getThunkRejectMessage } from "../../utils/thunkErrorMessage";
import { formatDateForInput } from "../../utils/dateUtils";

import {
  CLIENTS_LIST_PAGE_SIZE_STORAGE_KEY,
  DEFAULT_LIST_PAGE_SIZE,
} from "../../Utility/Pagination/listPaginationConstants";

/** Clients list table — default page size (GET /clients `limit`). */
export const CLIENTS_LIST_PAGE_SIZE = DEFAULT_LIST_PAGE_SIZE;
export { CLIENTS_LIST_PAGE_SIZE_STORAGE_KEY };

/** SaaS module row billing type — shared Add/Edit client + API values (`both` | `monthly` | `implementation`). */
export const SAAS_MODULE_BILLING_TYPE_DROPDOWN_OPTIONS = [
  { label: "Both", value: "both" },
  { label: "Monthly Only", value: "monthly" },
  { label: "Implementation Only", value: "implementation" },
];

/** ITP Access is a boolean on the API; Add/Edit client edit it as a Yes/No dropdown. */
export const ITP_ACCESS_DROPDOWN_OPTIONS = ["Yes", "No"];

/** Truthy test tolerant of flags the API hands back as 0/1 or "0"/"1" instead of booleans. */
function isTruthyFlag(value) {
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1" || v === "yes";
  }
  return Boolean(value);
}

/** Boolean (any API spelling) → "Yes"/"No" for {@link ITP_ACCESS_DROPDOWN_OPTIONS}. */
export const flagToYesNo = (value) => (isTruthyFlag(value) ? "Yes" : "No");

/** Values `domainRegistrar` carries over from the old `domainNameRegistered` boolean. */
const LEGACY_REGISTRAR_FLAG_VALUES = new Set(["0", "1", "true", "false"]);

/**
 * `domainRegistrar` → form value. Rows not yet re-entered carry the old boolean over as
 * "0"/"1", which is not a registrar name — show those as empty so the field reads blank
 * instead of showing a stray 0 or 1.
 */
export function domainRegistrarToFormValue(value) {
  if (value == null || typeof value === "boolean") return "";
  const text = String(value).trim();
  return LEGACY_REGISTRAR_FLAG_VALUES.has(text.toLowerCase()) ? "" : text;
}

const feeToFormString = (v) => (v != null && v !== "" ? String(v) : "");

/** Additional info on SaaS module row — API key remains `additionalFee` (text notes, not currency). */
function additionalInfoToFormString(row) {
  const raw =
    row.additionalFee ??
    row.additional_fee ??
    row.notes ??
    row.additionalInfo ??
    row.additional_info;
  if (raw == null || raw === "") return "";
  return String(raw).trim();
}

/**
 * Match GET /clients/:id/saas-modules row to master catalog `mod.id` (API may use `moduleId` or `module_id`).
 */
export function findClientSaasModuleRowByCatalogId(list, catalogModuleId) {
  if (!Array.isArray(list) || catalogModuleId == null) return null;
  const want = String(catalogModuleId);
  return list.find((m) => String(m.moduleId ?? m.module_id) === want) ?? null;
}

/**
 * Map one SaaS module assignment row to form field values (camelCase or snake_case from API).
 */
export function mapClientSaasModuleApiRowToFormFields(row) {
  if (row == null || typeof row !== "object") {
    return {
      implementationFee: "",
      monthlyFee: "",
      additionalFee: "",
      billingType: "both",
      startDate: "",
      endDate: "",
    };
  }
  const startRaw = row.startDate ?? row.start_date ?? "";
  const endRaw = row.endDate ?? row.end_date ?? "";
  return {
    implementationFee: feeToFormString(row.implementationFee ?? row.implementation_fee),
    monthlyFee: feeToFormString(row.monthlyFee ?? row.monthly_fee),
    additionalFee: additionalInfoToFormString(row),
    billingType: row.billingType ?? row.billing_type ?? "both",
    startDate: formatDateForInput(startRaw),
    endDate: formatDateForInput(endRaw),
  };
}

/** UI label → API enum for status filters and payloads */
const CLIENT_STATUS_TO_API = {
  Active: "ACTIVE",
  "On Hold": "ON_HOLD",
  Inactive: "CLOSED",
  /** Legacy label — still maps to CLOSED for filters / saved state */
  Closed: "CLOSED",
};

/**
 * Derive clients list from API: { data: { data: [clients], meta } }; maps name → clientName.
 */
export function getClientsListFromApiData(data) {
  const rawList = data?.data?.data ?? [];
  return rawList.map((c) => ({
    ...c,
    clientName: c.name ?? c.clientName,
  }));
}

export function getClientsMetaFromApiData(data) {
  return data?.data?.meta ?? null;
}

export function toClientStatusApi(value) {
  const v = String(value ?? "").trim();
  if (!v) return undefined;
  return CLIENT_STATUS_TO_API[value] ?? v.toUpperCase().replace(/\s+/g, "_");
}

/**
 * Params for `fetchClientsData` (GET /clients with page/limit), matching the clients list view.
 */
export function buildClientsListFetchParams({
  page = 1,
  limit = CLIENTS_LIST_PAGE_SIZE,
  listingFilter = {},
} = {}) {
  const f = listingFilter && typeof listingFilter === "object" ? listingFilter : {};
  const name = f.clientName?.trim() || undefined;
  const status = f.status && f.status !== "All" ? toClientStatusApi(f.status) : undefined;
  const contactPerson = f.contactPerson?.trim() || undefined;
  return {
    page,
    limit,
    ...(name ? { name } : {}),
    ...(status ? { status } : {}),
    ...(contactPerson ? { contactPerson } : {}),
  };
}

export function formToClientCreatePayload(formData) {
  return {
    name: formData.clientName?.trim() ?? "",
    qbClientName: formData.qbClientName?.trim() ?? "",
    contactPerson: formData.contactPerson?.trim() ?? "",
    contactEmail: formData.contactEmail?.trim() || null,
    status:
      CLIENT_STATUS_TO_API[formData.status] ??
      (formData.status ? String(formData.status).toUpperCase().replace(/\s+/g, "_") : "ACTIVE"),
    maxHoursMonthly: Number(formData.maxHoursMonthly) || 0,
    onHold: formData.status === "On Hold",
    isSaasClient: Boolean(formData.isSaasClient),
    notes: formData.notes?.trim() || null,
    serverHosted: formData.serverHosted?.trim() || null,
    ipAddress: formData.ipAddress?.trim() || null,
    dbIpAddress: formData.dbIpAddress?.trim() || null,
    dbLanguageId: formData.dbLanguageId ? String(formData.dbLanguageId) : null,
    mailHosted: formData.mailHosted?.trim() || null,
    domainName: formData.domainName?.trim() || null,
    dns: formData.dns?.trim() || null,
    domainRegistrar: formData.domainRegistrar?.trim() || null,
    itpAccess: isTruthyFlag(formData.itpAccess),
  };
}

export function formToClientUpdatePayload(formData) {
  return formToClientCreatePayload(formData);
}

/**
 * Legacy only: when GET /clients/:id returns a plain array on `data` or `list`, pick the matching row.
 * Normal shapes `{ data: client }` or `{ data: { client } }` are left as-is; see {@link apiClientToFormValues}.
 */
export function normalizeClientDetailResponseForForm(apiResponse, clientId) {
  if (apiResponse == null || typeof apiResponse !== "object") return apiResponse;
  const list = Array.isArray(apiResponse.data)
    ? apiResponse.data
    : Array.isArray(apiResponse.list)
      ? apiResponse.list
      : null;
  if (!list) return apiResponse;
  const row = list.find((c) => String(c?.id) === String(clientId));
  return row ? { data: row, success: apiResponse.success } : null;
}

export function extractAttachmentDownloadUrl(payload) {
  if (payload == null) return null;
  if (typeof payload === "string") return payload;
  return payload.data?.url ?? payload.url ?? null;
}

/** Normalize FileDropzone values to raw `File` instances for multipart upload. */
export function normalizeClientAttachmentFiles(files) {
  if (!Array.isArray(files)) return [];
  return files.map((item) => item?.file ?? item).filter(Boolean);
}

/**
 * Extract new client id from POST /clients response (supports nested API shapes).
 * @param {unknown} result — thunk payload (`response.data` from API)
 * @returns {string|null}
 */
export function extractCreatedClientId(result) {
  if (result == null || typeof result !== "object") return null;
  const nested = result.data;
  const candidates = [
    nested?.id,
    nested?.clientId,
    nested?.client?.id,
    result.id,
    result.clientId,
    result.client?.id,
  ];
  for (const value of candidates) {
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return null;
}

/**
 * Normalize GET /clients/:id/attachments bodies into an array of attachment rows.
 * @param {unknown} raw
 * @returns {object[]}
 */
export function extractClientAttachmentsList(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;

  const top = raw.data !== undefined ? raw.data : raw;
  if (Array.isArray(top)) return top;

  if (top && typeof top === "object") {
    for (const key of ["attachments", "data", "items", "list", "records"]) {
      const value = top[key];
      if (Array.isArray(value)) return value;
    }
    if (top.data && typeof top.data === "object" && Array.isArray(top.data.data)) {
      return top.data.data;
    }
  }

  return [];
}

const STATUS_FROM_API = { ACTIVE: "Active", ON_HOLD: "On Hold", CLOSED: "Inactive" };

function clientRecordFromDetailPayload(apiResponse) {
  const raw = apiResponse?.data ?? apiResponse;
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return {};
  return raw.client != null && typeof raw.client === "object" ? raw.client : raw;
}

export function apiClientToFormValues(apiResponse) {
  const c = clientRecordFromDetailPayload(apiResponse);
  const rawStatus = c.status;
  const normKey =
    rawStatus != null && String(rawStatus).trim() !== ""
      ? String(rawStatus).trim().toUpperCase().replace(/\s+/g, "_")
      : "";
  const status =
    STATUS_FROM_API[normKey] ?? (rawStatus ? String(rawStatus).replace(/_/g, " ") : "Active");
  const hours = c.maxHoursMonthly ?? c.hoursLimit;
  return {
    clientName: c.name ?? c.clientName ?? "",
    qbClientName: c.qbClientName ?? "",
    contactPerson: c.contactPerson ?? "",
    status,
    contactEmail: c.contactEmail ?? "",
    maxHoursMonthly: hours != null ? String(hours) : "",
    hoursLimitPeriod: c.hoursLimitPeriod ?? "Monthly",
    hoursLimit: c.hoursLimit != null ? String(c.hoursLimit) : "",
    notes: c.notes ?? "",
    isSaasClient: !!c.isSaasClient,
    saasPlanName: c.saasPlanName ?? "",
    saasBillingCycle: c.saasBillingCycle ?? "",
    saasModules: c.saasModules ?? {},
    serverHosted: c.serverHosted ?? "",
    ipAddress: c.ipAddress ?? "",
    dbIpAddress: c.dbIpAddress ?? "",
    dbLanguageId: c.dbLanguageId != null ? String(c.dbLanguageId) : "",
    mailHosted: c.mailHosted ?? "",
    domainName: c.domainName ?? "",
    dns: c.dns ?? "",
    domainRegistrar: domainRegistrarToFormValue(c.domainRegistrar),
    itpAccess: flagToYesNo(c.itpAccess),
  };
}

export function getStatusDisplay(client) {
  const raw = client?.status;
  if (raw === true) return { text: "Active", badgeVariant: "success" };
  if (raw === false) return { text: "In-Active", badgeVariant: "danger" };
  const lower = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (lower === "active") return { text: "Active", badgeVariant: "success" };
  if (lower === "inactive" || lower === "in-active")
    return { text: "In-Active", badgeVariant: "danger" };
  if (lower === "on hold" || lower === "on_hold")
    return { text: "On Hold", badgeVariant: "warning" };
  if (lower === "closed") return { text: "Inactive", badgeVariant: "secondary" };
  return { text: lower || "N/A", badgeVariant: "secondary" };
}

export function mapClientToTableRow(client) {
  return {
    clientName: client?.clientName ?? client?.name ?? "N/A",
    contactPerson: client?.contactPerson ?? "N/A",
    statusDisplay: getStatusDisplay(client),
  };
}

/** Message from `unwrap()` / thunks — avoids RTK noise like `"Rejected"` and raw `error.message` on strings. */
export function getErrorMessage(error) {
  if (error == null) return "";
  if (typeof error === "number" || typeof error === "boolean") return String(error);
  return getThunkRejectMessage(error, "");
}
